import React, { useEffect, useState } from 'react';
import { Box, Button, Snackbar } from '@mui/material';

const BUNDLED_VERSION = process.env.REACT_APP_VERSION || '0.00.0000';

/**
 * Fetches /version.json and prompts user to refresh when a newer version is deployed.
 * Only runs in production (version.json may not exist in dev).
 */
function VersionCheck() {
    const [showUpdateBanner, setShowUpdateBanner] = useState(false);

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') return;

        const checkVersion = () => {
            fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
                .then((res) => res.ok ? res.json() : null)
                .then((data) => {
                    if (data?.version && data.version !== BUNDLED_VERSION) {
                        setShowUpdateBanner(true);
                    }
                })
                .catch(() => {});
        };

        checkVersion();
        const interval = setInterval(checkVersion, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const handleRefresh = async () => {
        // Unregister any service workers that might be serving stale cached content
        if ('serviceWorker' in navigator) {
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map((r) => r.unregister()));
            } catch (_) {}
        }
        // Navigate to root with cache-buster to force fresh index.html and bundles (avoids path-specific caching)
        window.location.replace(window.location.origin + '/?_=' + Date.now());
    };

    // After a refresh, if we landed with the cache-bust param, clean the URL without reloading
    useEffect(() => {
        if (typeof window === 'undefined' || !window.history.replaceState) return;
        const params = new URLSearchParams(window.location.search);
        if (params.has('_')) {
            params.delete('_');
            const clean = params.toString()
                ? `${window.location.pathname}?${params}`
                : window.location.pathname || '/';
            window.history.replaceState({}, '', clean);
        }
    }, []);

    const handleClose = () => {
        setShowUpdateBanner(false);
    };

    return (
        <Snackbar
            open={showUpdateBanner}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            onClose={handleClose}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: 1,
                    boxShadow: 2,
                }}
            >
                <span>A new version is available.</span>
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={handleRefresh}
                >
                    Refresh
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClose}
                    sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.5)' }}
                >
                    Later
                </Button>
            </Box>
        </Snackbar>
    );
}

export default VersionCheck;
